import { useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import {
  type GlucoseReading,
  type GlucoseUnit,
  type NutritionEntry,
  type Point,
  cadenceMs,
  freshness,
  fromCanonicalMgdl,
} from "@jackedlogs/shared";

import type { UiTheme } from "../../theme";
import { makeSettingsCardStyles } from "../settingsCardStyles";
import { LineChart } from "../../charts/LineChart";
import { EmptyState } from "../../primitives/EmptyState";
import { StaleBadge } from "../../primitives/StaleBadge";

// Metabolic card (slice 3c): surfaces the glucose + nutrition the Health
// Connect merge imported, so the carbs↔glucose↔training picture is visible in
// JL. First overlay: a glucose trend + latest reading + recent carbs.
//
// Lifted to @jackedlogs/ui (S3, 2026-06-29): the glucose chart is the shared
// interactive LineChart (neutral line + an orange "now" dot, tap-a-point
// value). The metabolic rows come in as props (mobile passes useMetabolic +
// the profile glucose unit; the web console passes useMetabolicWeb) so the
// same card renders on both. Reduced-vision: large current values, one chart.
//
// HM-1 (2026-07-15): glucose and nutrition each answer for THEMSELVES. `hasData`
// used to be `glucose.length > 0 || nutrition.length > 0`, so one logged meal
// kept the whole card alive with zero glucose behind it and the reading row just
// silently vanished. From ~25-07 Antenore is over a month without a CGM: absent
// glucose is the NORMAL state here, and it has to LOOK absent.

function sameDay(aISO: string, bMs: number): boolean {
  return new Date(aISO).toDateString() === new Date(bMs).toDateString();
}

// Universal unit suffix — kept literal (not i18n) to match QuickGlucoseCard.
const unitLabel = (u: GlucoseUnit): string => (u === "mgdl" ? "mg/dL" : "mmol/L");

// Canonical mg/dL display target band (70–180 mg/dL) → the chosen unit.
const TARGET_LO_MGDL = 70;
const TARGET_HI_MGDL = 180;

type Props = {
  glucose: GlucoseReading[];
  nutrition: NutritionEntry[];
  glucoseUnit: GlucoseUnit;
  loading: boolean;
  locale: string;
  nowMs: number;
  theme: UiTheme;
};

export function MetabolicCard({
  glucose,
  nutrition,
  glucoseUnit: unit,
  loading,
  locale,
  nowMs,
  theme,
}: Props) {
  const { t } = useTranslation();
  const card = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }),
    [locale],
  );

  const points = useMemo<Point[]>(() => {
    // API is newest-first; chart wants chronological. Plot in the user's unit.
    return [...glucose].reverse().map((g) => ({
      x: Date.parse(g.takenAt),
      y: fromCanonicalMgdl(g.mgDl, unit),
      label: dateFmt.format(new Date(g.takenAt)),
    }));
  }, [glucose, dateFmt, unit]);

  const latest = glucose[0];
  const todayCarbs = useMemo(() => {
    let sum = 0;
    for (const n of nutrition)
      if (n.carbsG !== undefined && sameDay(n.takenAt, nowMs)) sum += n.carbsG;
    return Math.round(sum);
  }, [nutrition, nowMs]);

  // Per-metric, never shared: one carb entry must not vouch for the glucose
  // feed. `points` is already chronological, which is what freshness reads.
  const glucoseAge = useMemo(() => {
    const xs = points.map((p) => p.x);
    return { fresh: freshness(xs, nowMs), cadence: cadenceMs(xs) };
  }, [points, nowMs]);
  const showGlucoseAge = glucoseAge.fresh.known && glucoseAge.fresh.stale && latest;

  return (
    <View style={card.card}>
      <Text style={styles.head}>
        {t("mobile.metabolic.title", { defaultValue: "Metabolic" })}
      </Text>

      {loading && glucose.length === 0 && nutrition.length === 0 ? (
        <Text style={card.cardHelp}>
          {t("mobile.metabolic.loading", { defaultValue: "Loading…" })}
        </Text>
      ) : (
        <>
          {glucose.length === 0 ? (
            <EmptyState
              theme={theme}
              icon="info"
              title={t("mobile.metabolic.glucoseEmpty", {
                defaultValue: "No glucose readings",
              })}
              sub={t("mobile.metabolic.empty", {
                defaultValue:
                  "Connect Health Connect and sync to see glucose and nutrition here.",
              })}
            />
          ) : (
            <>
              {latest ? (
                <View style={styles.row}>
                  <Text style={styles.label}>
                    {t("mobile.metabolic.glucoseLatest", {
                      defaultValue: "Latest glucose",
                    })}
                  </Text>
                  <Text style={styles.value}>
                    {fromCanonicalMgdl(latest.mgDl, unit)}{" "}
                    <Text style={styles.unit}>{unitLabel(unit)}</Text>
                  </Text>
                </View>
              ) : null}

              {showGlucoseAge && latest ? (
                <View style={styles.ageRow}>
                  <StaleBadge
                    theme={theme}
                    atMs={Date.parse(latest.takenAt)}
                    locale={locale}
                    cadenceMs={glucoseAge.cadence}
                  />
                </View>
              ) : null}

              {points.length >= 2 ? (
                <LineChart
                  series={[{ key: "glucose", points, variant: "primary" }]}
                  yFormat={(n) => (unit === "mgdl" ? `${Math.round(n)}` : n.toFixed(1))}
                  theme={theme}
                  nowMs={nowMs}
                />
              ) : null}

              <Text style={card.cardHelp}>
                {t("mobile.metabolic.targetBand", {
                  defaultValue: "Typical target range {{lo}}–{{hi}} {{unit}}.",
                  lo: fromCanonicalMgdl(TARGET_LO_MGDL, unit),
                  hi: fromCanonicalMgdl(TARGET_HI_MGDL, unit),
                  unit: unitLabel(unit),
                })}
              </Text>
            </>
          )}

          <View style={styles.row}>
            <Text style={styles.label}>
              {t("mobile.metabolic.carbsToday", { defaultValue: "Carbs today" })}
            </Text>
            <Text style={styles.value}>
              {todayCarbs}{" "}
              <Text style={styles.unit}>
                {t("mobile.metabolic.grams", { defaultValue: "g" })}
              </Text>
            </Text>
          </View>
        </>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    head: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "700" },
    row: {
      flexDirection: "row",
      alignItems: "baseline",
      justifyContent: "space-between",
      marginTop: s.s2,
    },
    ageRow: { flexDirection: "row", marginTop: s.s1 },
    label: { color: p.ink2, fontSize: ty.sizes.base },
    value: {
      color: p.ink,
      fontSize: ty.sizes.lg,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    unit: { color: p.ink3, fontSize: ty.sizes.sm, fontWeight: "600" },
  });
}
